import {Component, effect, ElementRef, NgZone, OnDestroy, OnInit, Signal, AfterViewInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule, UntypedFormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {faMagic, faTimes} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {debounceTime, distinctUntilChanged, catchError, takeUntil} from 'rxjs/operators';
import {Observable, of, Subject} from 'rxjs';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';

import {ProductService} from '../../services/product/product.service';
import {HouseholdService} from '../../services/property/household.service';
import {CatalogService} from '../../services/catalog/catalog.service';
import {ContextService} from '../../services/context/context-signal.service';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {PageAction} from '../../components/app-shell/page-action.model';
import { AppLogger, AppLoggerService } from '../../core/logging';

import {CreateProductRequest} from '../../models/product';
import {CatalogProduct, CatalogSearchPage} from '../../models/catalog';
import {Context} from '../../models/context';

import {
  UiButtonComponent,
  UiInputComponent,
  UiSelectComponent,
  UiSelectOption,
  UiButtonGroupComponent,
  UiBadgeComponent, UiCardComponent
} from '../../shared/ui';
import {TagsInputComponent} from '../../components/tags-input/tags-input.component';
import {ProxyImageDirective} from '../../directives/proxy-image.directive';
import {PRODUCT_PLACEHOLDER_ICON} from '../../shared/product-placeholder-icon';

@Component({
  selector: 'app-add-new-product',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    UiButtonComponent,
    UiInputComponent,
    UiSelectComponent,
    UiButtonGroupComponent,
    UiBadgeComponent,
    TagsInputComponent,
    ProxyImageDirective,
    UiCardComponent
  ],
  templateUrl: './add-new-product.component.html',
  styleUrl: './add-new-product.component.scss'
})
export class AddNewProductComponent implements OnInit, OnDestroy, AfterViewInit {

  private readonly destroy$ = new Subject<void>();

  private readonly log: AppLogger;

  context: Signal<Context> = this.contextService.context;

  // FontAwesome icons
  faGenerate = faMagic;
  faClear = faTimes;
  faProductPlaceholder = PRODUCT_PLACEHOLDER_ICON;

  // Catalog search
  catalogProducts: CatalogProduct[] = [];
  showCatalogResults = false;
  selectedCatalogProduct: CatalogProduct | null = null;

  // Catalog pagination / infinite scroll
  private readonly catalogPageSize = 20;
  loadingMoreCatalog = false;
  private catalogCurrentPage = 0;
  private catalogTotal = 0;
  /** Monotonic id so stale (superseded) responses are discarded. */
  private catalogSearchSeq = 0;

  @ViewChild('catalogScrollAnchor') set catalogScrollAnchor(el: ElementRef<HTMLElement> | undefined) {
    this.observeCatalogAnchor(el?.nativeElement);
  }
  private catalogIntersectionObserver?: IntersectionObserver;
  private observedCatalogAnchor?: HTMLElement;
  providerBadges: { label: string; value: string }[] = [
    {label: 'Migros', value: 'migros'},
    {label: 'Open Foodfacts', value: 'off'}
  ];

  selectedProviders = new Set<string>(['migros', 'off']);

  householdId: number;
  isGeneratingBarcode = false;
  isGeneratedBarcodeReadonly = false;

  // Product mode: 'catalog' or 'internal'
  productMode: 'catalog' | 'internal' = 'catalog';
  hasAutoGeneratedInternalBarcode = false;

  // Store internal SKU when switching modes
  private storedInternalSku: string = '';
  private storedInternalSkuReadonly = false;

  // Default expiry helpers
  private readonly DAYS_PER_WEEK = 7;
  private readonly DAYS_PER_MONTH = 30;
  private readonly DAYS_PER_YEAR = 365;

  productForm = this.formBuilder.group({
    name: ['', Validators.required],
    code: ['', Validators.required],
    quantityType: ['fixed'],
    // Quantity value is a decimal representation and must be sent as string to the backend
    quantityValue: [''],
    quantityUnit: [''],
    brands: this.formBuilder.nonNullable.control([] as string[]),
    defaultExpiryValue: [null],
    defaultExpiryUnit: ['days'],
  });

  error: string | null = null;
  isSaving = false;

  // Select options
  quantityUnitOptions: UiSelectOption[] = [
    {value: '', label: '-- select unit --'},
    {value: 'g', label: 'g'},
    {value: 'kg', label: 'kg'},
    {value: 'ml', label: 'ml'},
    {value: 'l', label: 'l'},
    {value: 'piece', label: 'piece'},
    {value: 'unknown', label: 'unknown'}
  ];


  constructor(
    private contextService: ContextService,
    private productService: ProductService,
    private householdService: HouseholdService,
    private catalogService: CatalogService,
    private formBuilder: UntypedFormBuilder,
    private appLogger: AppLoggerService,
    private router: Router,
    private route: ActivatedRoute,
    private pageHeaderService: PageHeaderService,
    private zone: NgZone
  ) {
    this.log = this.appLogger.createForClass(AddNewProductComponent);

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      this.householdId = c.household.id;
      this.loadProviders(this.householdId);
    });
  }

  ngOnInit(): void {
    this.setupCatalogSearch();
    this.pageHeaderService.setPageTitle("Add New Product");

    // Re-publish actions whenever the form's validity changes (Save enabled state).
    this.productForm.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshPageActions());
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
    this.catalogIntersectionObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.refreshPageActions();
  }

  /**
   * Declarative toolbar actions:
   *  - Save: primary, pinned (always visible). Disabled when the form is
   *    invalid (and nothing was picked from the catalog) or while saving.
   *    Shows a spinner while saving.
   *  - Cancel: default secondary action — inline on desktop, auto-collapses
   *    into the ⋯ overflow on mobile. Disabled while saving.
   */
  private refreshPageActions(): void {
    const formInvalid = !this.productForm.valid && !this.selectedCatalogProduct;
    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'save',
        label: 'Save',
        variant: 'primary',
        pinned: true,
        disabled: formInvalid || this.isSaving,
        loading: this.isSaving,
        onClick: () => this.onSave(),
      },
      {
        type: 'button',
        id: 'cancel',
        label: 'Cancel',
        variant: 'neutral',
        disabled: this.isSaving,
        onClick: () => this.onCancel(),
      },
    ];
    this.pageHeaderService.setPageActions(actions);
  }

  setupCatalogSearch(): void {
    this.productForm.get('name')?.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => this.reloadCatalogFromFirstPage());
  }

  /** True while more catalog pages remain to be loaded for the current query. */
  get hasMoreCatalog(): boolean {
    return this.catalogProducts.length < this.catalogTotal;
  }

  /** Total number of catalog matches across all pages (for the results header). */
  get catalogResultTotal(): number {
    return this.catalogTotal;
  }

  /** Fetches a single catalog page for the current query + provider selection. */
  private fetchCatalogPage(query: string, page: number): Observable<CatalogSearchPage | null> {
    if (this.productMode !== 'catalog' || !query || query.length < 3) return of(null);
    const providers = Array.from(this.selectedProviders);
    return this.catalogService.searchCatalogProduct(query, providers, this.catalogPageSize, page).pipe(
      catchError(err => {
        this.log.error('Catalog search error', err, { query, providers, page });
        return of(null);
      })
    );
  }

  /** Runs a fresh catalog search from page 1, replacing the current results. */
  private reloadCatalogFromFirstPage(): void {
    const query = this.productForm.get('name')?.value ?? '';
    const seq = ++this.catalogSearchSeq;

    this.catalogCurrentPage = 0;
    this.catalogTotal = 0;
    this.catalogProducts = [];
    this.showCatalogResults = false;
    this.loadingMoreCatalog = false;

    if (this.productMode !== 'catalog' || query.length < 3) return;

    this.fetchCatalogPage(query, 1).subscribe(res => {
      if (seq !== this.catalogSearchSeq || !res) return;
      this.catalogProducts = res.items;
      this.catalogTotal = res.total;
      this.catalogCurrentPage = res.page;
      this.showCatalogResults = res.items.length > 0;
    });
  }

  /** Appends the next catalog page (triggered by scrolling to the bottom). */
  loadMoreCatalog(): void {
    if (this.loadingMoreCatalog || !this.hasMoreCatalog) return;

    const query = this.productForm.get('name')?.value ?? '';
    const seq = this.catalogSearchSeq;
    this.loadingMoreCatalog = true;

    this.fetchCatalogPage(query, this.catalogCurrentPage + 1).subscribe(res => {
      // A newer search may have started while this request was in flight.
      if (seq !== this.catalogSearchSeq) return;
      this.loadingMoreCatalog = false;
      if (!res) return;
      this.catalogProducts = [...this.catalogProducts, ...res.items];
      this.catalogTotal = res.total;
      this.catalogCurrentPage = res.page;
    });
  }

  /** Invalidates any in-flight catalog paging and resets counters. */
  private resetCatalogPagination(): void {
    this.catalogSearchSeq++;
    this.catalogCurrentPage = 0;
    this.catalogTotal = 0;
    this.loadingMoreCatalog = false;
  }

  private observeCatalogAnchor(el?: HTMLElement): void {
    if (el === this.observedCatalogAnchor) return;
    this.catalogIntersectionObserver?.disconnect();
    this.observedCatalogAnchor = el;
    if (!el) return;

    if (!this.catalogIntersectionObserver) {
      this.catalogIntersectionObserver = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) {
          // IntersectionObserver callbacks may run outside Angular's zone.
          this.zone.run(() => this.loadMoreCatalog());
        }
      }, { rootMargin: '200px' });
    }
    this.catalogIntersectionObserver.observe(el);
  }

  setProductMode(mode: 'catalog' | 'internal'): void {
    const previousMode = this.productMode;
    this.productMode = mode;

    // Store internal SKU when leaving internal mode
    if (previousMode === 'internal' && mode === 'catalog') {
      this.storedInternalSku = this.productForm.get('code')?.value || '';
      this.storedInternalSkuReadonly = this.isGeneratedBarcodeReadonly;
    }

    // Clear the code field when switching modes
    this.productForm.get('code')?.reset('');
    this.isGeneratedBarcodeReadonly = false;

    // Clear catalog results and selected product when switching modes
    this.catalogProducts = [];
    this.showCatalogResults = false;
    this.selectedCatalogProduct = null;
    this.resetCatalogPagination();

    // Restore internal SKU when switching back to internal mode
    if (mode === 'internal') {
      if (this.storedInternalSku) {
        this.productForm.get('code')?.setValue(this.storedInternalSku);
        this.isGeneratedBarcodeReadonly = this.storedInternalSkuReadonly;
      } else if (!this.hasAutoGeneratedInternalBarcode) {
        this.hasAutoGeneratedInternalBarcode = true;
        setTimeout(() => {
          this.generateInternalBarcode();
        }, 0);
      }
    }
  }

  private loadProviders(householdId: number): void {
    this.householdService.getProviderSettings(householdId).subscribe({
      next: (settings) => {
        const enabled = settings.filter(s => s.is_enabled).sort((a, b) => a.priority - b.priority);
        if (enabled.length === 0) return;
        this.providerBadges = enabled.map(s => ({ label: s.provider_name || s.provider_key, value: s.provider_key }));
        this.selectedProviders = new Set(enabled.map(s => s.provider_key));
      },
    });
  }

  toggleProvider(provider: string): void {
    if (this.selectedProviders.has(provider)) {
      this.selectedProviders.delete(provider);
    } else {
      this.selectedProviders.add(provider);
    }

    // Re-run search from the first page when the provider filter changes.
    this.reloadCatalogFromFirstPage();
  }

  isProviderSelected(provider: string): boolean {
    return this.selectedProviders.has(provider);
  }

  getProviderLogoPath(provider: string): string {
    if (!provider) return 'assets/img/provider-logos/unknown.png';
    return 'assets/img/provider-logos/' + provider.toLowerCase().replace(/ /g, '-') + '.png';
  }

  dismissCatalogResults(): void {
    this.catalogProducts = [];
    this.showCatalogResults = false;
    this.selectedCatalogProduct = null;
    this.resetCatalogPagination();
    this.refreshPageActions();
  }

  onCatalogProductClick(product: CatalogProduct): void {
    this.log.debug('Catalog product clicked', { product });
    this.selectedCatalogProduct = product;
    this.showCatalogResults = false;
    this.catalogProducts = [];
    this.resetCatalogPagination();

    this.productForm.patchValue({
      name: product.name,
      code: product.codes && product.codes.length > 0 ? product.codes[0].code : '',
      brands: product.brand ? [product.brand] : []
    });
    this.refreshPageActions();
  }

  clearSelectedCatalogProduct(): void {
    this.selectedCatalogProduct = null;
    this.productForm.patchValue({
      name: '',
      code: '',
      brands: []
    });
    this.refreshPageActions();
  }

  generateInternalBarcode(): void {
    if (!this.householdId || this.isGeneratingBarcode) {
      return;
    }

    this.isGeneratingBarcode = true;

    this.householdService.generateInternalBarcodes(this.householdId).subscribe({
      next: (resp) => {
        const value = resp && resp.value ? resp.value : undefined;
        if (value) {
          this.productForm.get('code')?.setValue(value);
          this.isGeneratedBarcodeReadonly = true;
        }
        this.isGeneratingBarcode = false;
      },
      error: (err) => {
        this.log.error('Error generating internal barcode', err);
        this.isGeneratingBarcode = false;
      }
    });
  }

  clearGeneratedBarcode(): void {
    this.isGeneratedBarcodeReadonly = false;
    this.productForm.get('code')?.reset('');
  }

  setDefaultExpiryUnit(unit: 'days' | 'weeks' | 'months' | 'years'): void {
    this.productForm.patchValue({defaultExpiryUnit: unit});
  }

  setQuantityType(type: 'fixed' | 'variable'): void {
    this.productForm.patchValue({quantityType: type});
  }

  /**
   * Normalizes a user-entered decimal string.
   * - trims whitespace
   * - converts comma decimal separator to dot
   * - returns null for empty/invalid values
   */
  normalizeDecimalString(value: any): string | null {
    const raw = String(value ?? '').trim();
    if (!raw) return null;

    const normalized = raw.replace(',', '.');
    // allow digits with optional single dot and fractional part (e.g. 1, 1., .5, 1.5)
    if (!/^(?:\d+\.?\d*|\.\d+)$/.test(normalized)) {
      return null;
    }

    // avoid sending trailing dot ("1." -> "1")
    return normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
  }

  parseIntValue(value: any): number | null {
    if (!value) return null;
    const num = parseInt(value, 10);
    return isNaN(num) ? null : num;
  }

  private normalizeUnit(unit?: string | null): string {
    return String(unit ?? '').trim().toLowerCase();
  }

  private detectUnitTypeFromUnit(unit?: string | null): string {
    const u = this.normalizeUnit(unit);

    if (u === 'g' || u === 'kg') {
      return 'weight';
    }
    if (u === 'ml' || u === 'l') {
      return 'volume';
    }
    if (u === 'piece') {
      return 'piece';
    }

    return '';
  }

  private clampNonNegativeInt(v: any): number {
    const n = Number(v);
    if (!isFinite(n) || isNaN(n)) {
      return 0;
    }
    return Math.max(0, Math.floor(n));
  }

  private expiryValueAndUnitToDays(value: any, unit: any): number {
    const v = this.clampNonNegativeInt(value);
    const u = String(unit || 'days').toLowerCase();

    if (v === 0) {
      return 0;
    }

    switch (u) {
      case 'years':
        return v * this.DAYS_PER_YEAR;
      case 'months':
        return v * this.DAYS_PER_MONTH;
      case 'weeks':
        return v * this.DAYS_PER_WEEK;
      case 'days':
      default:
        return v;
    }
  }

  onCancel(): void {
    this.router.navigate(['/products']);
  }

  onSave(): void {
    this.error = null;
    this.isSaving = true;
    this.refreshPageActions();

    this.log.info('Create Product', this.productForm.value);

    const form = this.productForm.value;
    const defaultExpiryDays = this.expiryValueAndUnitToDays(
      form.defaultExpiryValue,
      form.defaultExpiryUnit
    );
    const derivedUnitType = this.detectUnitTypeFromUnit(form.quantityUnit);

    const quantityValue = this.normalizeDecimalString(form.quantityValue);

    let createRequest: CreateProductRequest;

    if (this.selectedCatalogProduct) {
      createRequest = {
        catalog_product_id: this.selectedCatalogProduct.id,
        default_expiry_days: defaultExpiryDays
      };
    } else {
      createRequest = {
        codes: [this.productForm.value.code],
        name: this.productForm.value.name,
        quantity_type: (form.quantityType || 'fixed') as 'fixed' | 'variable',
        quantity: quantityValue ? {
          value: quantityValue,
          unit: form.quantityUnit,
          unit_type: derivedUnitType,
        } : undefined,
        brands: this.productForm.value.brands,
        default_expiry_days: defaultExpiryDays
      };
    }

    this.productService.addProduct(this.householdId, createRequest).subscribe({
      next: (resp) => {
        this.log.debug('Server response from create product', { resp });
        this.isSaving = false;
        this.refreshPageActions();

        if (resp && resp.id) {
          this.router.navigate(['/products', resp.id]);
        } else {
          this.router.navigate(['/products']);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.log.error('Error creating product', err);
        this.isSaving = false;
        this.refreshPageActions();

        if (err.status === HttpStatusCode.Conflict) {
          this.error = 'This product already exists in your household. Please check your existing products.';
        } else if (err.status === HttpStatusCode.InternalServerError) {
          this.error = 'An internal server error occurred. Please try again later or contact support if the problem persists.';
        } else {
          this.error = `An error occurred while creating the product: ${err.message || 'Unknown error'}`;
        }
      }
    });
  }
}
